import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer, ShieldCheck } from "lucide-react";

const Section = ({ title, children }) => (
  <section className="mt-6">
    <h2 className="font-heading text-lg font-bold text-white">{title}</h2>
    <div className="mt-2 text-[13px] text-zinc-400 leading-relaxed space-y-2">{children}</div>
  </section>
);

export default function Privacy() {
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <div className="no-print border-b border-zinc-800 flex items-center justify-between px-6 py-3" style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
        <Link to="/login" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back</Link>
        <button onClick={() => window.print()} className="flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white"><Printer className="h-4 w-4" /> Print / PDF</button>
      </div>
      <div className="max-w-3xl mx-auto p-6 sm:p-10" data-testid="privacy-page">
        <div className="flex items-center gap-2 text-blue-400"><ShieldCheck className="h-5 w-5" /><span className="text-[11px] uppercase tracking-widest font-bold">UC Engineer Pro</span></div>
        <h1 className="font-heading text-4xl font-extrabold text-white mt-2">Privacy Policy</h1>
        <p className="text-[12px] text-zinc-500 mt-2">Last updated: June 2026. This is a general template — review with your legal counsel before relying on it.</p>

        <Section title="Who we are">
          <p>UC Engineer Pro ("we", "us") provides a Cisco Unified Communications migration and CUCM 15 upgrade workspace. This policy explains what we collect and why.</p>
        </Section>
        <Section title="Information we collect">
          <p>• <strong>Account data</strong> you provide: name, email, password (stored hashed), workspace and team membership.</p>
          <p>• <strong>Workspace content</strong> you enter: sites, device inventory, runbook progress, licenses, certificates, E911 and related records.</p>
          <p>• <strong>Usage analytics</strong>: pages/routes visited, referrer, device type, browser and operating system, and approximate location (country/city) derived from your IP address, plus a random visitor/session identifier.</p>
          <p>• <strong>Payment data</strong>: handled by our payment processor (Stripe). We do not store full card numbers.</p>
        </Section>
        <Section title="How we use it">
          <p>To operate and secure the service, provide features, process subscriptions, understand usage, prevent abuse, and comply with legal and export/sanctions obligations (including restricting access from certain jurisdictions).</p>
        </Section>
        <Section title="Cookies & similar technologies">
          <p>We use strictly necessary cookies for authentication and session management, and local browser storage for preferences and analytics identifiers. You can clear these at any time in your browser.</p>
        </Section>
        <Section title="Third parties">
          <p>We share limited data with service providers that help us run UC Engineer Pro, including Stripe (payments), our transactional email provider (team invites), and an IP geolocation lookup used for analytics and regional access control. We do not sell your personal data.</p>
        </Section>
        <Section title="Data retention & security">
          <p>We retain data for as long as your account is active or as needed to provide the service and meet legal obligations. We apply reasonable technical and organizational safeguards, including encryption in transit and hashed passwords.</p>
        </Section>
        <Section title="Your rights">
          <p>Depending on your location, you may have rights to access, correct, export, or delete your personal data. Contact us to exercise these rights.</p>
        </Section>
        <Section title="Regional availability">
          <p>Access to UC Engineer Pro is not available from certain countries due to legal and sanctions requirements. Requests from those regions are blocked.</p>
        </Section>
        <Section title="Contact">
          <p>Questions about this policy? Contact your workspace administrator or the account owner of record.</p>
        </Section>
        <p className="mt-8 text-[12px] text-zinc-600">See also our <Link to="/terms" className="text-blue-400 hover:text-blue-300 underline">Terms of Service</Link>.</p>
      </div>
    </div>
  );
}
